import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import { BASE_PATH_TO_LEADERBOARD } from "../../../base_routes_paths/corporate_challenges_paths";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

// ── Theme ─────────────────────────────────────────────────────────────────────
const BG      = "#0d0d14";
const CARD_BG = "#13131f";
const CARD_1  = "#1a0d2e";
const BORDER  = "#2a2a3d";
const PURPLE  = "#7c3aed";
const GLOW    = "rgba(124,58,237,0.35)";
const PRI     = "#ffffff";
const SEC     = "#8b8ba0";
const MEDAL   = { 1: "#D4A017", 2: "#6B7280", 3: "#A0522D" };

// ── Podium sizing ─────────────────────────────────────────────────────────────
const PODIUM_SIZE = {
  1: { avatar: 160, card: 300, mt: 0,  minH: 320, namePx: 36, scorePx: 52 },
  2: { avatar: 120, card: 240, mt: 40, minH: 240, namePx: 30, scorePx: 38 },
  3: { avatar: 120, card: 240, mt: 40, minH: 240, namePx: 30, scorePx: 38 },
  4: { avatar: 100, card: 200, mt: 80, minH: 160, namePx: 24, scorePx: 30 },
  5: { avatar: 100, card: 200, mt: 80, minH: 160, namePx: 24, scorePx: 30 },
};

// ── Podium Card ───────────────────────────────────────────────────────────────

const PodiumCard = ({ user, rank }) => {
  const isFirst     = rank === 1;
  const medal       = MEDAL[rank] ?? SEC;
  const { avatar: avatarSize, card: cardW, mt, minH, namePx, scorePx } = PODIUM_SIZE[rank] ?? PODIUM_SIZE[5];
  const hasTrophy   = rank <= 3;
  const borderColor = hasTrophy ? medal : PURPLE;

  return (
    <Box sx={{ flex: `0 0 ${cardW}px`, display: "flex", flexDirection: "column", alignItems: "center", mt: `${mt}px` }}>
      <Box sx={{ position: "relative", mb: `${-avatarSize * 0.28}px`, zIndex: 1 }}>
        {hasTrophy && (
          <EmojiEventsIcon sx={{ position: "absolute", top: -(avatarSize * 0.28), right: -(avatarSize * 0.1), fontSize: avatarSize * 0.34, color: medal, filter: `drop-shadow(0 0 8px ${medal}aa)`, zIndex: 2 }} />
        )}
        <Box sx={{ width: avatarSize, height: avatarSize, borderRadius: "50%", border: `${isFirst ? 4 : 3}px solid ${borderColor}`, boxShadow: `0 0 ${isFirst ? 28 : 18}px ${borderColor}66`, position: "relative" }}>
          <Avatar src={user.ProfileImage} alt={user.Name} sx={{ width: "100%", height: "100%", backgroundColor: "#1e1e30" }} />
          <Box sx={{ position: "absolute", bottom: 0, right: 0, width: avatarSize * 0.32, height: avatarSize * 0.32, borderRadius: "50%", backgroundColor: borderColor, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${BG}`, zIndex: 3 }}>
            <Typography sx={{ fontSize: avatarSize * 0.14, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{rank}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: "100%", borderRadius: 1.5, border: `1px solid ${isFirst ? PURPLE : BORDER}`, backgroundColor: isFirst ? CARD_1 : CARD_BG, boxShadow: `0 0 28px ${GLOW}, inset 0 0 40px ${GLOW}`, pt: `${avatarSize * 0.28 + 16}px`, pb: isFirst ? 4 : 2.5, px: 2, textAlign: "center", minHeight: `${minH}px`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography fontWeight={800} color={PRI} mb={0.5} sx={{ fontSize: namePx, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {user.Name}
        </Typography>
        {isFirst && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} mb={1.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: PURPLE }} />
            <Typography variant="body2" fontWeight={700} color={PURPLE} letterSpacing={1.5}>CURRENT CHAMPION</Typography>
          </Stack>
        )}
        <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.75}>
          <Typography fontWeight={900} color={PRI} lineHeight={1} sx={{ fontSize: scorePx }}>
            {user.ChallengeAtmpt?.replace(/[^0-9]/g, "") || "—"}
          </Typography>
          <Typography fontWeight={700} color={SEC} letterSpacing={1} sx={{ fontSize: scorePx * 0.45 }}>
            {user.ChallengeAtmpt?.replace(/[0-9\s]/g, "").toUpperCase() || ""}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

// ── List Row ──────────────────────────────────────────────────────────────────

const ColHeader = () => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, pb: 1.5, mb: 0.5 }}>
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, width: 48 }}>RANK</Typography>
    <Box sx={{ width: 52, flexShrink: 0 }} />
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, flex: 1 }}>PARTICIPANT</Typography>
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, minWidth: 90, textAlign: "right" }}>POINTS</Typography>
  </Stack>
);

const ListRow = ({ user, rank }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 2, borderRadius: 1, backgroundColor: "#1a1a2a", border: `1px solid ${BORDER}` }}>
    <Typography variant="h6" fontWeight={700} sx={{ width: 48, flexShrink: 0, color: SEC, fontVariantNumeric: "tabular-nums" }}>
      {String(rank).padStart(2, "0")}
    </Typography>
    <Box sx={{ width: 52, height: 52, borderRadius: "50%", border: `1px solid ${BORDER}`, flexShrink: 0, overflow: "hidden", backgroundColor: "#1e1e30" }}>
      <Avatar src={user.ProfileImage} alt={user.Name} sx={{ width: "100%", height: "100%", backgroundColor: "transparent" }} />
    </Box>
    <Typography variant="h5" fontWeight={700} color={PRI} noWrap sx={{ flex: 1, minWidth: 0 }}>{user.Name}</Typography>
    <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ flexShrink: 0, minWidth: 90, justifyContent: "flex-end" }}>
      <Typography variant="h5" fontWeight={900} color={PRI} lineHeight={1}>{user.ChallengeAtmpt?.replace(/[^0-9]/g, "") || "—"}</Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: SEC }}>{user.ChallengeAtmpt?.replace(/[0-9\s]/g, "").toUpperCase() || ""}</Typography>
    </Stack>
  </Stack>
);

// ── Skeletons ─────────────────────────────────────────────────────────────────

const PodiumSkeleton = ({ size }) => (
  <Box sx={{ flex: `0 0 ${size}px`, display: "flex", flexDirection: "column", alignItems: "center" }}>
    <Skeleton variant="circular" width={size * 0.5} height={size * 0.5} sx={{ bgcolor: "#1e1e30", mb: "-20px" }} />
    <Skeleton variant="rounded" width="100%" height={size * 0.7} sx={{ borderRadius: 2, bgcolor: "#1e1e30" }} />
  </Box>
);

const RowSkeleton = () => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 2, borderRadius: 1, backgroundColor: "#1a1a2a", border: `1px solid ${BORDER}` }}>
    <Skeleton variant="text" width={48} sx={{ bgcolor: "#1e1e30" }} />
    <Skeleton variant="circular" width={52} height={52} sx={{ bgcolor: "#1e1e30" }} />
    <Box sx={{ flex: 1 }}><Skeleton variant="text" width="50%" sx={{ bgcolor: "#1e1e30" }} /></Box>
    <Skeleton variant="text" width={70} sx={{ bgcolor: "#1e1e30" }} />
  </Stack>
);

// ── Main component ────────────────────────────────────────────────────────────

const CombinedLeaderboard = () => {
  const { corporateID } = useParams();
  const { token }       = AUTH_CONTEXT();

  const [users, setUsers]         = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetCorporateLeaderboard`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
            body: JSON.stringify({ CorporateID: corporateID }),
          }
        );
        const data = await response.json();
        if (response.status === 200 && data.status === 200) {
          setUsers(data.data?.RankedData?.slice(0, 11) ?? []);
        } else {
          toast.error(data.error || "Failed to load leaderboard", { position: "bottom-left", autoClose: 3000, hideProgressBar: true });
        }
      } catch (error) {
        toast.error(error.message, { position: "bottom-left", autoClose: 5000, theme: "light" });
      } finally {
        setIsLoading(false);
      }
    };
    fetchLeaderboard();
  }, [token, corporateID]);

  const top5 = users.slice(0, 5);
  const rest = users.slice(5);

  // Podium order: 4 | 2 | 1 | 3 | 5
  const podiumOrder = top5.length === 5
    ? [{ user: top5[3], rank: 4 }, { user: top5[1], rank: 2 }, { user: top5[0], rank: 1 }, { user: top5[2], rank: 3 }, { user: top5[4], rank: 5 }]
    : top5.map((u, i) => ({ user: u, rank: i + 1 }));

  const half     = Math.ceil(rest.length / 2);
  const colLeft  = rest.slice(0, half);
  const colRight = rest.slice(half);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: BG, px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3, "& *": { color: `${SEC} !important` }, "& .MuiBreadcrumbs-separator": { color: `${BORDER} !important` } }}>
        <Link underline="hover" href={BASE_PATH_TO_HOME} sx={{ color: SEC }}>Dashboard</Link>
        <Link underline="hover" sx={{ color: SEC }}>Corporate Challenges</Link>
        <Link underline="hover" href={BASE_PATH_TO_LEADERBOARD} sx={{ color: SEC }}>Leaderboard</Link>
        <Link underline="hover" href={`${BASE_PATH_TO_LEADERBOARD}/${corporateID}`} sx={{ color: SEC }}>{corporateID}</Link>
        <Typography fontWeight={500} sx={{ color: PRI }}>Combined Leaderboard</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, background: `linear-gradient(135deg, ${PURPLE} 0%, #1a0d2e 100%)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 16px ${GLOW}` }}>
            <EmojiEventsIcon sx={{ color: "#FFD700", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color={PRI} lineHeight={1.2}>Combined Leaderboard</Typography>
            <Typography variant="body2" sx={{ color: SEC }}>Corporate ID: <strong style={{ color: PRI }}>{corporateID}</strong></Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Podium */}
      {isLoading ? (
        <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={2} mb={5}>
          {[185, 240, 300, 240, 185].map((w, i) => <PodiumSkeleton key={i} size={w} />)}
        </Stack>
      ) : top5.length > 0 ? (
        <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={2} mb={5}>
          {podiumOrder.map(({ user, rank }) => (
            <PodiumCard key={user._id} user={user} rank={rank} />
          ))}
        </Stack>
      ) : null}

      {/* Rest — two columns, all remaining participants */}
      {(isLoading || rest.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <ColHeader />
            <Stack spacing={1.5}>
              {isLoading
                ? Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)
                : colLeft.map((user, i) => <ListRow key={user._id} user={user} rank={i + 6} />)
              }
            </Stack>
          </Box>
          <Box>
            <ColHeader />
            <Stack spacing={1.5}>
              {isLoading
                ? Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)
                : colRight.map((user, i) => <ListRow key={user._id} user={user} rank={i + 6 + half} />)
              }
            </Stack>
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && users.length === 0 && (
        <Box sx={{ py: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <EmojiEventsIcon sx={{ fontSize: 48, color: SEC, opacity: 0.4 }} />
          <Typography variant="body1" fontWeight={500} sx={{ color: SEC }}>No participants yet</Typography>
        </Box>
      )}
    </Box>
  );
};

export default CombinedLeaderboard;
